import { useCallback, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';

import {
  formatDisplayName,
  formatLoginId,
  getWorkspaceNameHelperText,
  isDisplayNameValid,
  isLoginIdValid,
} from '../signupUtils';
import type { AccountDetailsState } from '../signupUtils';
import { useValidateAccountName } from './useValidateAccountName';

type UseWorkspaceNameFieldsOptions = {
  displayName: string;
  setField: (data: Partial<AccountDetailsState>) => void;
  inviteId?: string;
};

export const useWorkspaceNameFields = ({
  displayName,
  setField,
  inviteId,
}: UseWorkspaceNameFieldsOptions) => {
  const [availabilityState, checkAvailability] = useValidateAccountName();
  const [loginId, setLoginId] = useState('');
  const [nameModified, setNameModified] = useState(false);

  const handleDisplayNameChange = useCallback(
    (value: string) => {
      const formattedName = formatDisplayName(value);
      setLoginId(formatLoginId(formattedName));
      setField({ displayName: formattedName });
    },
    [setField],
  );

  const handleDisplayNameBlur = useCallback(() => setNameModified(true), []);

  useDebounce(
    () => {
      if (!isLoginIdValid(loginId)) return;
      checkAvailability({ loginId, inviteId });
    },
    500,
    [loginId],
  );

  const loginIdAvailable = availabilityState.value?.available ?? true;
  const loginIdError = availabilityState.value?.message;
  const isCheckingAvailability = availabilityState.loading;

  const loginIdConfirmedAvailable =
    !!availabilityState.value &&
    !isCheckingAvailability &&
    loginIdAvailable &&
    !loginIdError;

  const validDisplayName = nameModified
    ? isDisplayNameValid(displayName)
    : true;

  const validLoginId = nameModified ? isLoginIdValid(loginId) : true;

  const helperText = getWorkspaceNameHelperText({
    displayName,
    loginId,
    isModified: nameModified,
    isAvailable: loginIdAvailable,
    availabilityError: loginIdError,
  });

  return {
    loginId,
    handleDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    validLoginId,
    helperText,
    loginIdAvailable,
    loginIdError,
    loginIdConfirmedAvailable,
    isCheckingAvailability,
  };
};
